export type PwaSurface = 'client' | 'admin' | 'ib'

const META: Record<
  PwaSurface,
  { manifest: string; theme: string; title: string; appleIcon: string }
> = {
  client: {
    manifest: '/pwa/client/manifest.webmanifest',
    theme: '#1E4DB7',
    title: 'Perfect Predict',
    appleIcon: '/pwa/client/icon-192.png',
  },
  admin: {
    manifest: '/pwa/admin/manifest.webmanifest',
    theme: '#C9A227',
    title: 'Perfect Predict Admin',
    appleIcon: '/pwa/admin/icon-192.png',
  },
  ib: {
    manifest: '/pwa/ib/manifest.webmanifest',
    theme: '#1F8A70',
    title: 'Perfect Predict IB',
    appleIcon: '/pwa/ib/icon-192.png',
  },
}

export function resolvePwaSurface(pathname = window.location.pathname): PwaSurface {
  const host = window.location.hostname.toLowerCase()
  if (host.startsWith('admin.') || pathname.startsWith('/admin')) return 'admin'
  if (pathname.startsWith('/ib')) return 'ib'
  return 'client'
}

function ensureLink(rel: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[data-pp-pwa="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('data-pp-pwa', rel)
    document.head.appendChild(el)
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v))
  return el
}

function ensureMeta(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[data-pp-pwa="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('data-pp-pwa', name)
    document.head.appendChild(el)
  }
  el.setAttribute('name', name)
  el.setAttribute('content', content)
}

/** Swap manifest / theme for the active surface (client, admin, IB). */
export function applyPwaSurface(surface: PwaSurface = resolvePwaSurface()) {
  const cfg = META[surface]
  ensureLink('manifest', { rel: 'manifest', href: cfg.manifest })
  ensureLink('apple-touch-icon', { rel: 'apple-touch-icon', href: cfg.appleIcon })
  ensureMeta('theme-color', cfg.theme)
  ensureMeta('apple-mobile-web-app-capable', 'yes')
  ensureMeta('apple-mobile-web-app-status-bar-style', 'black-translucent')
  ensureMeta('apple-mobile-web-app-title', cfg.title)
  ensureMeta('mobile-web-app-capable', 'yes')
  if (!document.title || document.title.startsWith('Perfect Predict')) {
    document.title = cfg.title
  }
  return surface
}

let swRegistered = false

export async function registerPwaServiceWorker() {
  if (swRegistered) return
  if (!('serviceWorker' in navigator)) return
  if (!window.isSecureContext) return
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' })
    swRegistered = true
    reg.update().catch(() => undefined)
  } catch {
    /* ignore SW failures in unsupported contexts */
  }
}

export function bootPwa() {
  applyPwaSurface()
  void registerPwaServiceWorker()
}
